import React from 'react';
import { Link } from 'react-router-dom';
import { useSchoolInfo } from '../hooks/useSchoolInfo';

export default function Footer() {
  const schoolInfo = useSchoolInfo();
  return (
    <footer className="bg-white text-[#5A6961] pt-16 pb-12 border-t border-[#d9e5d4]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Top Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10 pb-12 border-b border-[#d9e5d4]">
          
          {/* Brand Col */}
          <div className="lg:col-span-4 space-y-4">
            <Link to="/" className="inline-block focus:outline-none focus:ring-2 focus:ring-[#88A68E] rounded-lg">
              <img
                src="https://assets.auxodev.dk/assets/7e157867-df92-46f8-b5ca-20179d4d9044/odder-lille-friskole-bt.png"
                alt="Odder Lille Friskole Logo"
                className="h-14 sm:h-16 w-auto object-contain"
              />
            </Link>

            <div className="pt-2 flex items-center gap-3">
              <Link
                to="/skriv-dit-barn-op"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-bold text-white bg-[#264A35] hover:bg-[#1D2923] transition-all shadow-xs"
              >
                <i className="fa-solid fa-pen-nib" aria-hidden="true" />
                <span>Skriv dit barn op</span>
              </Link>
              <Link
                to="/besoeg-skolen"
                className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-xs font-semibold text-[#264A35] bg-white hover:bg-[#f8faf5] transition-all border border-[#d9e5d4] shadow-xs"
              >
                <span>Besøg skolen</span>
              </Link>
            </div>
          </div>

          {/* Quick Links 1: Ny på skolen */}
          <div className="lg:col-span-3 space-y-3">
            <h3 className="text-sm font-bold tracking-wider uppercase text-[#1D2923]">Ny på skolen</h3>
            <ul className="space-y-2 text-sm text-[#5A6961]">
              <li>
                <Link to="/side/vaerdier" className="hover:text-[#264A35] font-medium transition-colors">Værdier & Fundament</Link>
              </li>
              <li>
                <Link to="/side/derfor-vaelger-familier-os" className="hover:text-[#264A35] font-medium transition-colors">Derfor vælger familier os</Link>
              </li>
              <li>
                <Link to="/side/optagelse-og-indmeldelse" className="hover:text-[#264A35] font-medium transition-colors">Optagelse og indmeldelse</Link>
              </li>
              <li>
                <Link to="/besoeg-skolen" className="hover:text-[#264A35] font-medium transition-colors">Besøg skolen</Link>
              </li>
              <li>
                <Link to="/skriv-dit-barn-op" className="hover:text-[#264A35] font-medium transition-colors">Skriv dit barn op</Link>
              </li>
            </ul>
          </div>

          {/* Quick Links 2: Hverdagen & Praktisk */}
          <div className="lg:col-span-2 space-y-3">
            <h3 className="text-sm font-bold tracking-wider uppercase text-[#1D2923]">Hverdagen</h3>
            <ul className="space-y-2 text-sm text-[#5A6961]">
              <li>
                <Link to="/side/undervisning-og-faglighed" className="hover:text-[#264A35] font-medium transition-colors">Undervisning</Link>
              </li>
              <li>
                <Link to="/side/fritteren-og-klub" className="hover:text-[#264A35] font-medium transition-colors">Fritteren & Klub</Link>
              </li>
              <li>
                <Link to="/side/traditioner-og-lejre" className="hover:text-[#264A35] font-medium transition-colors">Traditioner & Ture</Link>
              </li>
              <li>
                <Link to="/side/praktisk-info" className="hover:text-[#264A35] font-medium transition-colors">Praktisk info</Link>
              </li>
              <li>
                <Link to="/gallerier" className="hover:text-[#264A35] font-medium transition-colors">Billedgallerier</Link>
              </li>
              <li>
                <Link to="/nyheder" className="hover:text-[#264A35] font-medium transition-colors">Nyheder</Link>
              </li>
            </ul>
          </div>

          {/* Contact Col */}
          <div className="lg:col-span-3 space-y-3">
            <h3 className="text-sm font-bold tracking-wider uppercase text-[#1D2923]">Kontakt & Info</h3>
            <div className="space-y-2.5 text-sm text-[#5A6961]">
              <div className="font-semibold text-[#1D2923]">{schoolInfo.name}</div>
              <div className="flex items-start gap-2.5">
                <i className="fa-solid fa-location-dot mt-1 text-[#264A35] text-xs" aria-hidden="true" />
                <span>{schoolInfo.address}<br />{schoolInfo.city}</span>
              </div>
              <div className="flex items-center gap-2.5">
                <i className="fa-solid fa-phone text-[#264A35] text-xs" aria-hidden="true" />
                <a
                  href={`tel:${schoolInfo.phoneRaw}`}
                  className="hover:text-[#264A35] font-medium whitespace-nowrap transition-colors"
                >
                  {schoolInfo.phone}
                </a>
              </div>
              <div className="flex items-center gap-2.5">
                <i className="fa-solid fa-envelope text-[#264A35] text-xs" aria-hidden="true" />
                <a
                  href={`mailto:${schoolInfo.email}`}
                  className="hover:text-[#264A35] font-medium whitespace-nowrap transition-colors"
                >
                  {schoolInfo.email}
                </a>
              </div>
              <div className="pt-1 text-xs text-[#7A8B82]">
                CVR: <span className="font-mono">{schoolInfo.cvr}</span>
              </div>
            </div>
          </div>

        </div>

        {/* Bottom Bar */}
        <div className="pt-8 flex flex-col sm:flex-row items-center justify-between text-xs text-[#7A8B82] gap-4">
          <div>
            &copy; {new Date().getFullYear()} {schoolInfo.name}. Alle rettigheder forbeholdes.
          </div>
          <div className="flex items-center gap-6">
            <Link to="/side/om-skolen" className="hover:text-[#1D2923] transition-colors">Om skolen</Link>
            <Link to="/medarbejdere" className="hover:text-[#1D2923] transition-colors">Medarbejdere</Link>
            <Link to="/side/tilsyn-og-evaluering" className="hover:text-[#1D2923] transition-colors">Tilsyn & Dokumenter</Link>
            <Link to="/kontakt" className="hover:text-[#1D2923] transition-colors">Kontakt</Link>
          </div>
        </div>

      </div>
    </footer>
  );
}
